import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { showNotification } from '@/utils/notificationHelper';
import { useHistory } from 'react-router-dom';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { request } from '@/request';
import { connectSocket, releaseSocket } from '@/services/socketClient';

export const NOTIFICATION_ROUTES = {
    leave_request: '/LeaveRequestList',
    leave_approval: '/LeaveRequestList',
    overtime_request: '/overtime-extrahours',
    overtime_approval: '/overtime-extrahours',
    salary_slip: '/salary-management',
    leave_benefit: '/leavebenefitrequests',
    leave_benefit_status: '/leavesummary',
    appraisal: '/appraisaldashboard',
    complaint: '/suggestion-list',
    suggestion: '/suggestion-list',
    announcement: '/announcement-feeds',
    profile_nudge: '/employee'
};

// ------------------------------------------------------------------
// Utility Functions for Notification Styling (Shared across components)
// ------------------------------------------------------------------

export const getColor = (notification) => {
    const type = notification.notificationType;
    const colors = {
        leave_request: '#1890ff',        // Blue
        leave_approval: '#1890ff',       // Blue
        leave_benefit: '#13c2c2',        // Cyan
        leave_benefit_status: '#13c2c2', // Cyan
        overtime_request: '#fa8c16',     // Orange
        overtime_approval: '#fa8c16',    // Orange
        salary_slip: '#722ed1',          // Purple
        announcement: '#eb2f96',         // Magenta
        system_announcement: '#eb2f96',  // Magenta
        appraisal: '#2f54eb',            // Geekblue
        complaint: '#f5222d',            // Red
        suggestion: '#a0d911',           // Lime
        reminder: '#faad14',
        profile_nudge: '#722ed1',
        profile_status: '#fa541c',
        designation: '#1890ff',
        general: '#d9d9d9'
    };
    return colors[type] || '#d9d9d9';
};

export const getBadges = (notification) => {
    const message = (notification.message || '').toLowerCase();
    const relatedData = notification.relatedData || {};
    const badges = [];

    // Extract request type badge (Priority: relatedData > notificationType > message parsing)
    let requestType = null;
    const rType = (relatedData.requestType || '').toLowerCase();

    if (rType === 'encashment' || message.includes('encashment')) {
        requestType = { text: '💵 ENCASHMENT', color: '#13c2c2' };
    } else if (rType === 'carryforward' || message.includes('carryforward') || message.includes('carry forward')) {
        requestType = { text: '📅 CARRY FORWARD', color: '#13c2c2' };
    } else if (rType === 'annual' || message.includes('annual')) {
        requestType = { text: '🏖️ ANNUAL', color: '#1890ff' };
    } else if (rType === 'sick' || message.includes('sick')) {
        requestType = { text: '🤒 SICK', color: '#1890ff' };
    } else if (rType === 'casual' || message.includes('casual')) {
        requestType = { text: '📝 CASUAL', color: '#1890ff' };
    } else if (rType === 'half day' || message.includes('half day')) {
        requestType = { text: '🌗 HALF DAY', color: '#1890ff' };
    } else if (notification.notificationType === 'leave_request' || notification.notificationType === 'leave_approval' || relatedData.requestId) {
        requestType = { text: '📄 LEAVE', color: '#1890ff' };
    } else if (message.includes('extra hours')) {
        requestType = { text: '🕒 EXTRA HOURS', color: '#fa8c16' };
    } else if (relatedData.overtimeId || notification.notificationType === 'overtime_request' || notification.notificationType === 'overtime_approval' || message.includes('overtime')) {
        requestType = { text: '🕒 OVERTIME', color: '#fa8c16' };
    } else if (notification.notificationType === 'salary_slip') {
        requestType = { text: '💰 SALARY SLIP', color: '#722ed1' };
    } else if (notification.notificationType === 'appraisal') {
        requestType = { text: '📊 APPRAISAL', color: '#2f54eb' };
    } else if (notification.notificationType === 'complaint') {
        requestType = { text: '❗ COMPLAINT', color: '#f5222d' };
    } else if (notification.notificationType === 'suggestion') {
        requestType = { text: '💡 SUGGESTION', color: '#a0d911' };
    } else if (notification.notificationType === 'leave_balance') {
        requestType = { text: '⚖️ LEAVE BALANCE', color: '#13c2c2' };
    } else if (notification.notificationType === 'announcement' || notification.notificationType === 'system_announcement') {
        requestType = { text: '📣 ANNOUNCEMENT', color: '#eb2f96' };
    }

    if (requestType) badges.push(requestType);

    // Extract status badge (Priority: relatedData.status > message parsing)
    const status = (relatedData.status || '').toLowerCase();
    let statusBadge = null;

    if (status === 'approved' || message.includes('approved')) {
        statusBadge = { text: '✅ APPROVED', bgColor: '#f6ffed', textColor: '#52c41a', borderColor: '#b7eb8f' };
    } else if (status === 'rejected' || message.includes('rejected')) {
        statusBadge = { text: '❌ REJECTED', bgColor: '#fff1f0', textColor: '#f5222d', borderColor: '#ffa39e' };
    } else if (status === 'pending' || message.includes('pending')) {
        statusBadge = { text: '⏳ PENDING', bgColor: '#fff7e6', textColor: '#fa8c16', borderColor: '#ffd591' };
    }

    if (statusBadge) badges.push(statusBadge);

    return badges;
};

// Hook Starts
export const useNotifications = () => {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const history = useHistory();

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [activeType, setActiveType] = useState('all');
    const [tabCounts, setTabCounts] = useState({
        all: 0,
        leave_request: 0,
        leave_approval: 0,
        leave_benefit: 0,
        overtime_request: 0,
        overtime_approval: 0,
        break_alert: 0,
        announcement: 0,
        salary_slip: 0,
        others: 0
    });

    const fetchNotifications = useCallback(async (pageNum = 1, type = 'all', isTabSwitch = false) => {
        setIsLoading(true);
        try {
            const result = await request.get({ entity: `notifications?page=${pageNum}&limit=15&type=${type}` });
            if (result && result.success) {
                const newNotifs = result.data || [];
                setNotifications(prev => (pageNum === 1 || isTabSwitch) ? newNotifs : [...prev, ...newNotifs]);
                setUnreadCount(result.unreadCount || 0);
                setHasMore(newNotifs.length === 15);
            }
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchCounts = useCallback(async () => {
        try {
            const result = await request.get({ entity: 'notifications/counts' });
            if (result && result.success) {
                setTabCounts(result.counts);
                setUnreadCount(result.counts.all || 0);
            }
        } catch (err) {
            console.error('Failed to fetch notification counts:', err);
        }
    }, []);

    const handleTabChange = useCallback((type) => {
        setActiveType(type);
        setPage(1);
        setHasMore(true);
        setNotifications([]); // Clear current list
        setIsLoading(true); // Show spinner immediately
        fetchNotifications(1, type, true);
    }, [fetchNotifications]);

    const loadMore = useCallback(() => {
        if (!isLoading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchNotifications(nextPage, activeType);
        }
    }, [isLoading, hasMore, page, fetchNotifications, activeType]);

    const markAsRead = async (id) => {
        try {
            const result = await request.patch({ entity: `notifications/read/${id}` });
            if (result && result.success) {
                setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Failed to mark notification as read:', err);
        }
    };

    const markAllAsRead = async () => {
        try {
            const result = await request.patch({ entity: 'notifications/read-all' });
            if (result && result.success) {
                setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
                setUnreadCount(0);
            }
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };

    const urlBase64ToUint8Array = useCallback((base64String) => {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }, []);

    const subscribeToPush = useCallback(async () => {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            try {
                let permission = Notification.permission;

                if (permission === 'default') {
                    permission = await Notification.requestPermission();
                }

                if (permission !== 'granted') {
                    console.warn('[useNotifications] Permission not granted for notifications');
                    return;
                }

                const registration = await navigator.serviceWorker.ready;

                const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
                if (!vapidPublicKey) {
                    console.error('[useNotifications] VAPID public key is missing from environment');
                    return;
                }

                const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

                const subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: convertedVapidKey
                });


                await request.post({
                    entity: 'notifications/subscribe',
                    jsonData: { subscription }
                });

            } catch (err) {
                console.error('[useNotifications] ERROR during push subscription process:', err);
                console.error('[useNotifications] Error details:', err.message, err.name);
            }
        } else {
            console.warn('[useNotifications] Push messaging is not supported in this browser');
        }
    }, [urlBase64ToUint8Array]);

    useEffect(() => {
        if (!currentAdmin?._id) return;

        fetchNotifications(1, activeType);
        fetchCounts();

        const newSocket = connectSocket({
            userId: currentAdmin._id,
            role: currentAdmin.role
        });
        if (!newSocket) return;

        subscribeToPush(); // Trigger push subscription on load

        newSocket.on('connect', () => {
            console.log(`[SocketService] Connection ESTABLISHED for user ${currentAdmin._id}. Socket ID: ${newSocket.id}`);
        });

        newSocket.on('connect_error', (err) => {
            console.error('[SocketService] Connection ERROR:', err.message, err.description);
        });

        newSocket.on('disconnect', (reason) => {
            console.warn('[SocketService] Connection DISCONNECTED:', reason);
        });

        newSocket.on('new_notification', (data) => {
            console.log('[SocketService] Incoming notification:', data);

            // Only add to list if it belongs to current active tab
            const typeGroups = {
                leave_request: ['leave_request'],
                leave_approval: ['leave_approval'],
                leave_benefit: ['leave_benefit'],
                overtime_request: ['overtime_request'],
                overtime_approval: ['overtime_approval'],
                break_alert: ['break_alert'],
                announcement: ['announcement', 'system_announcement'],
                salary_slip: ['salary_slip'],
                others: ['appraisal', 'complaint', 'suggestion', 'profile_status', 'designation', 'reminder', 'general', 'leave_benefit_status']
            };

            const fitsTab = activeType === 'all' || (typeGroups[activeType] && typeGroups[activeType].includes(data.notificationType));

            if (fitsTab) {
                setNotifications(prev => {
                    if (prev.some(n => n._id === data._id)) return prev;
                    return [data, ...prev];
                });
            }

            // Always update global count and tab badges
            fetchCounts();

            // Trigger global event for AttendanceReport to open Modal + Loop Sound
            // Fire for ALL notification types — GlobalReminderModal filters internally
            window.dispatchEvent(new CustomEvent('reminder_received', { detail: data }));

            // Trigger global event for GlobalProfileAlert to refresh and show acknowledgement banner
            if (data.notificationType === 'appraisal') {
                window.dispatchEvent(new CustomEvent('refresh-profile-data', { detail: data }));
            }

            // Trigger global event for HR to see Profile Nudge alert
            if (data.notificationType === 'profile_nudge') {
                window.dispatchEvent(new CustomEvent('profile_nudge_received', { detail: data }));
            }

            const badges = getBadges(data);

            showNotification.open({
                message: <span style={{ fontWeight: 'bold' }}>{data.title}</span>,
                description: (
                    <div>
                        <div style={{ marginBottom: badges.length > 0 ? '8px' : 0 }}>{data.message}</div>
                        {badges.length > 0 && (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                                {badges.map((badge, idx) => (
                                    <span
                                        key={idx}
                                        style={{
                                            display: 'inline-block',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            backgroundColor: badge.bgColor || badge.color,
                                            color: badge.textColor || '#ffffff',
                                            border: badge.borderColor ? `1px solid ${badge.borderColor}` : 'none'
                                        }}
                                    >
                                        {badge.text}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                ),
                placement: 'bottomRight',
                icon: (
                    <img
                        src="/dymaxtech.png"
                        alt="DymaxTech"
                        style={{ width: 32, height: 'auto', marginTop: -4 }}
                    />
                ),
                style: {
                    borderLeft: `4px solid ${getColor(data)}`,
                    borderRadius: '4px',
                },
                onClick: () => {
                    const route = NOTIFICATION_ROUTES[data.notificationType];
                    if (route) {
                        history.push(route);
                        showNotification.destroy();
                    }
                }
            });
        });

        newSocket.on('reminder_handled', (data) => {
            window.dispatchEvent(new CustomEvent('reminder_handled', { detail: data }));
            fetchCounts();
        });

        newSocket.on('OFFBOARDING_UPDATE', (data) => {
            console.log('[useNotifications] Offboarding update received:', data);
            window.dispatchEvent(new CustomEvent('offboarding_update_received', { detail: data }));
        });

        newSocket.on('connect_error', (err) => {
            console.error('[useNotifications] Socket connection error:', err);
        });

        return () => {
            newSocket.off('connect');
            newSocket.off('connect_error');
            newSocket.off('disconnect');
            newSocket.off('new_notification');
            newSocket.off('reminder_handled');
            newSocket.off('OFFBOARDING_UPDATE');
            releaseSocket();
        };
    }, [currentAdmin?._id, fetchNotifications, subscribeToPush]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                document.title = unreadCount > 0 ? `(${unreadCount}) DymaxTech CRM` : 'DymaxTech CRM';
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [unreadCount]);

    const deleteNotification = async (id) => {
        try {
            // Optimistic update
            const notif = notifications.find(n => n._id === id);
            if (notif && !notif.isRead) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
            setNotifications(prev => prev.filter(n => n._id !== id));

            const result = await request.delete({ entity: 'notifications', id });
            if (!result || !result.success) {
                console.error('Delete failed, fetching fresh list...');
                fetchNotifications();
            }
        } catch (err) {
            console.error('Failed to delete notification:', err);
            fetchNotifications();
        }
    };

    return {
        notifications,
        unreadCount,
        tabCounts,
        activeType,
        markAsRead,
        markAllAsRead,
        subscribeToPush,
        fetchNotifications,
        fetchCounts,
        deleteNotification,
        handleTabChange,
        loadMore,
        isLoading,
        hasMore
    };
};

